import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AsyncCreatableSelect from "react-select/async-creatable";
import { fromJS } from "immutable";
import { PlusIcon, X } from "lucide-react";
import { createItem, search } from "../api/ProjectApi";
import { ContentTypes } from "../constants/action-types";

const Menu = (props: JSX.IntrinsicElements["div"]) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        border: "1px solid #e5e7eb",
        marginTop: 8,
        position: "absolute",
        zIndex: 9999,
      }}
      {...props}
    />
  );
};

const Blanket = (props: JSX.IntrinsicElements["div"]) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: "fixed",
      zIndex: 9998,
    }}
    {...props}
  />
);

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
}: {
  children?: React.ReactNode;
  readonly isOpen: boolean;
  readonly target: React.ReactNode;
  readonly onClose: () => void;
}) => (
  <div>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const ActivitySender = ({ title, addButtonText, projectId, eventData, handleChange }) => {
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const project = useSelector((state: any) => state.projectReducer.getIn(["project"]));

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (eventData.organizer) {
      if (eventData.organizer === currentUser.get("email")) {
        const currentAssignee = {
          value: currentUser.get("email"),
          label: currentUser.get("displayName"),
          data: {
            data: {
              email: { value: currentUser.get("email"), type: "string" },
              name: { value: currentUser.get("displayName"), type: "string" },
            },
            iconUrl: currentUser.get("avatarUrl"),
          },
        };
        setSelectedItems([currentAssignee]);
      } else {
        search({
          projectId,
          pageSize: 1,
          types: ["CONTACT", "COMPANY"],
          filters: {
            email: {
              operator: "is",
              values: [eventData.organizer],
            },
          },
        }).then((result) => {
          const datas = fromJS(result)
            .getIn(["pageResult", "content"])
            .map((item) => ({
              value: item.getIn(["data", "email", "value"]),
              label: `${item.getIn(["data", "name", "value"])}`,
              data: item,
            }))
            .toJS();
          const distinctDatas = Array.from(
            new Map(datas.map((item) => [item.value, item])).values()
          );
          setSelectedItems(distinctDatas);
        });
      }
    } else {
      setSelectedItems([]);
    }
  }, [eventData.organizer, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      search({
        projectId,
        pageSize: 10,
        types: ["CONTACT", "COMPANY"],
        filters: {
          email: {
            operator: "regex",
            values: [inputValue],
          },
        },
      }).then((result) => {
        const datas = fromJS(result)
          .getIn(["pageResult", "content"])
          .filter((item) => !!item.getIn(["data", "email", "value"]))
          .map((item) => ({
            value: item.getIn(["data", "email", "value"]),
            label: item.getIn(["data", "name", "value"]),
            data: item,
          }))
          .toJS();
        const distinctDatas = Array.from(new Map(datas.map((item) => [item.value, item])).values());
        callback(distinctDatas);
      });
    },
    [projectId]
  );

  const handleCreate = async (inputValue: string) => {
    const newItemData = {
      data: {
        name: { value: inputValue },
        email: { value: inputValue },
      },
      projectId: project.get("id"),
      type: ContentTypes.CONTACT,
    };
    await createItem(newItemData);
    handleChange("organizer", inputValue);
    setIsOpen(false);
  };

  const handleRemove = () => {
    handleChange("organizer", "");
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">{title}</label>
        </div>
        <div>
          <Dropdown
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            target={
              <div
                className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 shadow-sm ${
                  eventData.organizer ? "bg-gray-100 text-gray-500" : "cursor-pointer"
                }`}
                onClick={() => (eventData.organizer ? null : setIsOpen((prev) => !prev))}
              >
                <PlusIcon className="w-3 h-3" />
                <span>{addButtonText}</span>
              </div>
            }
          >
            <AsyncCreatableSelect
              autoFocus
              backspaceRemovesValue={false}
              components={{ DropdownIndicator: null, IndicatorSeparator: null }}
              controlShouldRenderValue={false}
              // hideSelectedOptions={false}
              tabSelectsValue={false}
              isClearable={false}
              menuIsOpen
              isMulti
              value={selectedItems}
              onChange={(values) => {
                if (values) {
                  const organizers = values.map((value: any) => value.value).join(",");
                  handleChange("organizer", organizers);
                } else {
                  handleChange("organizer", "");
                }
                setIsOpen(false);
              }}
              cacheOptions
              defaultOptions
              loadOptions={loadOptions}
              formatCreateLabel={(input) => `Add "${input}"`}
              isValidNewOption={(inputValue, selectValue, selectOptions) => {
                if (!emailRegex.test(inputValue)) return false;
                return !selectOptions.some(
                  (opt) => opt.value.toLowerCase() === inputValue.toLowerCase()
                );
              }}
              onCreateOption={handleCreate}
              placeholder={t("Search or enter an email to add new")}
              formatOptionLabel={(option) => {
                if (option["__isNew__"]) {
                  return option.label;
                }
                const contactOrCompany = fromJS(option.data || {});
                return (
                  <div
                    className={"w-full hover:text-blue-600 flex items-center space-x-2"}
                    key={option.value}
                  >
                    {contactOrCompany.get("iconUrl") ? (
                      <img
                        src={contactOrCompany.get("iconUrl")}
                        alt={
                          contactOrCompany.getIn(["data", "name", "value"]) ||
                          contactOrCompany.getIn(["data", "email", "value"])
                        }
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full">
                        <svg
                          className="h-full w-full text-gray-300"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-base">
                        {contactOrCompany.getIn(["data", "name", "value"])}
                      </div>
                      {contactOrCompany.getIn(["data", "email", "value"]) && (
                        <p className="text-sm text-gray-600">
                          {contactOrCompany.getIn(["data", "email", "value"])}
                        </p>
                      )}
                    </div>
                  </div>
                );
              }}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minWidth: 300,
                  minHeight: 38,
                  margin: 8,
                  borderRadius: 8,
                  backgroundColor: state.isDisabled ? "#fbf9fa" : "white",
                  borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
                  boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
                  ":hover": {
                    borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                  },
                  fontSize: 14,
                }),
                menu: (provided) => ({
                  ...provided,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: "#6b7280",
                  fontWeight: 500,
                }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: 14,
                  fontWeight: 500,
                  backgroundColor: state.isSelected
                    ? "#dbeafe"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                  color: state.isDisabled ? "#6b7280" : "#111827",
                  ":active": {
                    backgroundColor: "#e5e7eb",
                  },
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </Dropdown>
        </div>
      </div>
      <div className="p-2 space-y-4">
        {selectedItems.map((item) => {
          const contactOrCompany = fromJS(item.data);
          return (
            <div key={item.value} className="w-full flex items-center justify-between gap-2">
              <div className="flex items-center gap-x-2">
                {contactOrCompany.get("iconUrl") ? (
                  <img
                    className="h-6 w-6 flex-none rounded-full bg-gray-50"
                    src={contactOrCompany.get("iconUrl")}
                    alt={item.label}
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full">
                    <svg
                      className="h-full w-full text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                )}
                <div
                  title={`${item.label} (${item.value})`}
                  className="text-sm font-semibold leading-6 text-gray-900"
                >
                  {item.label}
                </div>
                <div className="hidden sm:block ml-2 text-sm font-medium leading-6 text-gray-400">
                  {item.value}
                </div>
              </div>
              <div className="flex items-center gap-x-2">
                <X
                  className="w-4 h-4 text-gray-500 cursor-pointer"
                  onClick={() => handleRemove()}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivitySender;
